/**
 * Модерация публикаций: вызовы `/api/posts*` (`reels/web/api_posts.py`) и ключи react-query.
 * Функция на ручку; отказ — `ApiFailure` как есть (страницы читают `.code`, `.items[0]`;
 * у 409 `STALE` свежая публикация лежит в теле ошибки).
 */
import { apiFetch, query } from "@/lib/api";
import type { CalendarItem } from "@/lib/postSchedule";
import type { ContentPlan } from "@/lib/postsTabs";
import type {
  Crop,
  PostAccount,
  PostAccountNetwork,
  PostCandidate,
  PostCity,
  PostDetail,
  PostListItem,
  PostStatus,
  PromptVersion,
} from "@/lib/postPlan";

/** Аккаунт в списке сетей (без пометок публикации `checked` / `also_in`). */
export type AccountRow = Omit<PostAccount, "checked" | "also_in">;
export type AccountNetworkRow = Omit<PostAccountNetwork, "accounts"> & { accounts: AccountRow[] };

export type AccountsResult = { networks: AccountNetworkRow[]; salons: PostCity[]; crm_error: string | null };

export type AccountInput = {
  network: string;
  handle: string;
  name?: string | null;
  crm_location_id?: number | null;
  city_name?: string | null;
};

/** Переданный `crm_location_id: null` — «федеральный»; ключ не передан — не трогаем. */
export type AccountPatch = {
  name?: string | null;
  crm_location_id?: number | null;
  city_name?: string | null;
  archived?: boolean;
};

export type PromptsResult = {
  current: Record<string, PromptVersion>;
  history: Record<string, PromptVersion[]>;
};

export type PromptVersionFull = PromptVersion & {
  scope: string;
  gens: {
    gen_id: number;
    plan_id: number;
    network: string;
    text: string | null;
    title: string | null;
    hashtags: string | null;
    asset_path: string | null;
    date_create: string | null;
  }[];
};

export type PlanPatch = {
  base_modify: string;
  accounts?: number[];
  networks?: Record<string, Record<string, unknown>>;
};

export type GenerateResult = { plan: PostDetail } & Record<string, unknown>;

export const postsKeys = {
  all: ["posts"] as const,
  accounts: ["posts", "accounts"] as const,
  prompts: ["posts", "prompts"] as const,
  promptVersions: ["posts", "prompts", "version"] as const,
  promptVersion: (id: number) => ["posts", "prompts", "version", id] as const,
  candidates: (variantId?: number, kind?: "photo" | "video") =>
    ["posts", "candidates", variantId ?? null, kind ?? "photo"] as const,
  plans: (status: PostStatus) => ["posts", "list", status] as const,
  contentPlan: ["posts", "content-plan"] as const,
  calendar: (network: string) => ["posts", "calendar", network] as const,
  plan: (id: number) => ["posts", "plan", id] as const,
};

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

const base = "/api/posts";

export const loadAccounts = () => apiFetch<AccountsResult>(`${base}/accounts`);
export const createAccount = (b: AccountInput) => apiFetch<AccountNetworkRow[]>(`${base}/accounts`, json("POST", b));
export const patchAccount = (id: number, b: AccountPatch) =>
  apiFetch<AccountNetworkRow[]>(`${base}/accounts/${id}`, json("PATCH", b));

export const loadPrompts = () => apiFetch<PromptsResult>(`${base}/prompts`);
export const loadPromptVersion = (id: number) => apiFetch<PromptVersionFull>(`${base}/prompts/versions/${id}`);
export const savePrompt = (b: { scope: string; text: string; note?: string | null }) =>
  apiFetch<PromptsResult>(`${base}/prompts`, json("POST", b));
export const restorePrompt = (versionId: number) =>
  apiFetch<PromptsResult>(`${base}/prompts/versions/${versionId}/restore`, json("POST"));

/** `kind` по умолчанию — `photo` (как раньше, сигнатура не меняется — вызовов с `variantId` уже
 * несколько); `video` — готовые варианты-видео без проверки метки. */
export const loadCandidates = (variantId?: number, kind?: "photo" | "video") =>
  apiFetch<PostCandidate[]>(`${base}/candidates` + query({ variant_id: variantId, kind }));
export const loadContentPlan = () => apiFetch<ContentPlan>(`${base}/content-plan`);
/** Календарь сети (№385): даты выкладки неотменённых публикаций, черновики тоже. */
export const loadCalendar = (network: string) => apiFetch<CalendarItem[]>(`${base}/calendar` + query({ network }));
export const loadPlans = (status: PostStatus) => apiFetch<PostListItem[]>(base + query({ status }));
export const createPlan = (variantId: number) => apiFetch<PostDetail>(base, json("POST", { variant_id: variantId }));
/** Карусель: порядок элементов публикации = порядок `variantIds`, первый — обложка. */
export const createPlanFromMany = (variantIds: number[]) =>
  apiFetch<PostDetail>(base, json("POST", { variant_ids: variantIds }));
export const loadPlan = (id: number) => apiFetch<PostDetail>(`${base}/${id}`);
export const patchPlan = (id: number, b: PlanPatch) => apiFetch<PostDetail>(`${base}/${id}`, json("PATCH", b));

// --- элементы карусели (§5 спеки «Публикация из нескольких элементов и видео в ней») --------------

/** Добавить в конец карусели; уже добавленные варианты сервер молча пропускает. */
export const addMedia = (id: number, baseModify: string, variantIds: number[]) =>
  apiFetch<PostDetail>(`${base}/${id}/media`, json("POST", { base_modify: baseModify, variant_ids: variantIds }));
/** Убрать элемент; `base_modify` — query-параметром (у `DELETE` в проекте тела нет ни у одной ручки). */
export const removeMedia = (id: number, mediaId: number, baseModify: string) =>
  apiFetch<PostDetail>(`${base}/${id}/media/${mediaId}` + query({ base_modify: baseModify }), { method: "DELETE" });
/** Новый порядок — ПОЛНЫЙ список id строк `post_plan_media` (`lib/postMedia.move`). */
export const reorderMedia = (id: number, baseModify: string, order: number[]) =>
  apiFetch<PostDetail>(`${base}/${id}/media`, json("PATCH", { base_modify: baseModify, order }));
/** Рамка элемента по сетям; значение `null` снимает рамку этой сети, остальные сети не трогаются. */
export const setMediaCrop = (id: number, mediaId: number, baseModify: string, crop: Record<string, Crop | null>) =>
  apiFetch<PostDetail>(`${base}/${id}/media/${mediaId}`, json("PATCH", { base_modify: baseModify, crop }));
export const generatePlan = (id: number, networks?: string[]) =>
  apiFetch<GenerateResult>(`${base}/${id}/generate`, json("POST", { networks: networks ?? null }));
export const takeGen = (id: number, code: string, genId: number, baseModify: string) =>
  apiFetch<PostDetail>(`${base}/${id}/networks/${code}/take`, json("POST", { gen_id: genId, base_modify: baseModify }));
// --- переходы статуса (№285: на сеть; `networks` не передан — как раньше, на всю публикацию) ------

/** `networks` — коды утверждаемых сетей; без них утверждаются ВСЕ готовые. */
export const approvePlan = (id: number, baseModify: string, networks?: string[]) =>
  apiFetch<PostDetail>(`${base}/${id}/approve`, json("POST", { base_modify: baseModify, networks: networks ?? null }));
/** `networks` — какие сети вернуть в черновик; без них — все утверждённые сети публикации. */
export const unapprovePlan = (id: number, baseModify: string, networks?: string[]) =>
  apiFetch<PostDetail>(`${base}/${id}/unapprove`, json("POST", { base_modify: baseModify, networks: networks ?? null }));
/** `networks` — отменить названные сети (остальные живут дальше); без них — всю публикацию. */
/** Дата выкладки сети (№383): `2026-10-23T12:00`, `null` — снять. Правится и у утверждённой сети. */
export const schedulePlan = (id: number, baseModify: string, network: string, date: string | null) =>
  apiFetch<PostDetail>(`${base}/${id}/schedule`, json("POST", { base_modify: baseModify, network, date }));
export const cancelPlan = (id: number, baseModify: string, networks?: string[]) =>
  apiFetch<PostDetail>(`${base}/${id}/cancel`, json("POST", { base_modify: baseModify, networks: networks ?? null }));
